import React from 'react';
import {
    DatePicker as MuiDatePicker,
    type DatePickerProps as UntypedMuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import {PickersDay, type PickersDayProps, pickersDayClasses, type PickersActionBarProps} from '@mui/x-date-pickers';
import {type Moment} from 'moment-timezone';
import MuiIcon from '@mui/material/Icon';
import MuiIconButton from '@mui/material/IconButton';
import {useFormControl} from '@mui/material/FormControl';
import DialogActions from '@mui/material/DialogActions';
import {useLocaleText} from '@mui/x-date-pickers/internals';
import MuiButton from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {ModernFamlyContext} from '@famly/mf_system_provider';
import {useBreakpoints} from '@famly/mf_util_breakpoints';
import {MF_DATE_DIALOG_PADDING, useTheme} from '@famly/mf_theme';
import {Icon as ModernFamlyIcon} from '@famly/mf_data-display_icon';
import {DatePickersLocalizationProvider} from '@famly/mf_date-and-time_date-pickers-localizations';
import {useDataProps} from '@famly/mf_ts-utils_data-aria-props';
import {type TextFieldProps} from '@famly/mf_input_text';

import {dateViewsOrder} from './date-views-order';

type MuiDatePickerProps = UntypedMuiDatePickerProps<Moment>;

export type DatePickerProps = {
    error?: TextFieldProps['error'];
    placeholder?: TextFieldProps['placeholder'];
    required?: TextFieldProps['required'];
    size?: TextFieldProps['size'];
    fullWidth?: TextFieldProps['fullWidth'];
    name?: TextFieldProps['name'];
    id?: TextFieldProps['id'];
    autoFocus?: MuiDatePickerProps['autoFocus'];
    closeOnSelect?: MuiDatePickerProps['closeOnSelect'];
    defaultCalendarMonth?: MuiDatePickerProps['defaultCalendarMonth'];
    defaultValue?: MuiDatePickerProps['defaultValue'];
    disabled?: MuiDatePickerProps['disabled'];
    disableFuture?: MuiDatePickerProps['disableFuture'];
    disablePast?: MuiDatePickerProps['disablePast'];
    inputRef?: MuiDatePickerProps['inputRef'];
    maxDate?: MuiDatePickerProps['maxDate'];
    minDate?: MuiDatePickerProps['minDate'];
    onChange?: MuiDatePickerProps['onChange'];
    onAccept?: MuiDatePickerProps['onAccept'];
    onClose?: MuiDatePickerProps['onClose'];
    onMonthChange?: MuiDatePickerProps['onMonthChange'];
    onOpen?: MuiDatePickerProps['onOpen'];
    onYearChange?: MuiDatePickerProps['onYearChange'];
    open?: MuiDatePickerProps['open'];
    shouldDisableDate?: MuiDatePickerProps['shouldDisableDate'];
    shouldDisableMonth?: MuiDatePickerProps['shouldDisableMonth'];
    shouldDisableYear?: MuiDatePickerProps['shouldDisableYear'];
    value?: MuiDatePickerProps['value'];
} & DataProps;

export const DatePicker = ({
    size,
    error: errorFromProps,
    required,
    placeholder,
    name,
    id,
    fullWidth,
    autoFocus,
    closeOnSelect,
    defaultCalendarMonth,
    defaultValue,
    disabled: disabledFromProps,
    disableFuture,
    disablePast,
    inputRef,
    maxDate,
    minDate,
    onChange,
    onAccept,
    onClose,
    onMonthChange,
    onOpen,
    onYearChange,
    open,
    shouldDisableDate,
    shouldDisableMonth,
    shouldDisableYear,
    value,
    ...rest
}: DatePickerProps) => {
    // We keep the internal state of the DatePicker component so we can fire the `onAccept` callback
    // when the picker's input field is blurred
    const [state, setState] = React.useState<Parameters<NonNullable<MuiDatePickerProps['onChange']>>>([
        value ?? defaultValue ?? null,
        {validationError: null},
    ]);

    const {disabled: disabledFromFormControl, error: errorFromFormControl} = useFormControl() || {};

    const disabled = disabledFromProps ?? disabledFromFormControl;
    const error = errorFromProps ?? errorFromFormControl;

    const dataProps = useDataProps(rest);

    const {breakpoints} = useTheme();
    const desktopMediaQuery = breakpoints.up('tabletLandscape');
    const {isTabletLandscapeAndLarger} = useBreakpoints();

    const onBlur = React.useCallback(() => {
        // On mobile devices, the onBlur callback is fired
        // when the dialog is opened. Hence we ignore onBlur
        // on mobile devices.
        if (!isTabletLandscapeAndLarger) {
            return;
        }

        onAccept?.(state[0]);
    }, [isTabletLandscapeAndLarger, onAccept, state]);

    const slotProps: MuiDatePickerProps['slotProps'] = React.useMemo(() => {
        return {
            textField: {
                fullWidth,
                InputProps: {
                    onBlur,
                    size,
                    error,
                    required,
                    placeholder,
                    name,
                    id,
                    ...dataProps,
                },
            },
            inputAdornment: {
                sx: {
                    marginRight: size === 'compact' ? '-2px' : undefined,
                },
            },
            popper: {
                slotProps: {
                    root: {
                        'data-e2e-class': 'date-picker-popper',
                    },
                },
            },
        };
    }, [fullWidth, id, onBlur, size, error, required, placeholder, name, dataProps]);

    const {handleOnOpen, handleOnClose} = useSyncContextWithOpenState({onOpen, onClose});

    return (
        <DatePickersLocalizationProvider>
            <MuiDatePicker
                views={dateViewsOrder}
                slotProps={slotProps}
                autoFocus={autoFocus}
                closeOnSelect={closeOnSelect}
                defaultCalendarMonth={defaultCalendarMonth}
                defaultValue={defaultValue}
                disabled={disabled}
                disableFuture={disableFuture}
                disablePast={disablePast}
                fixedWeekNumber={6}
                inputRef={inputRef}
                maxDate={maxDate}
                minDate={minDate}
                onClose={handleOnClose}
                onChange={(...args) => {
                    setState(args);
                    onChange?.(...args);
                }}
                onAccept={onAccept}
                onMonthChange={onMonthChange}
                onOpen={handleOnOpen}
                onYearChange={onYearChange}
                open={open}
                shouldDisableDate={shouldDisableDate}
                shouldDisableMonth={shouldDisableMonth}
                shouldDisableYear={shouldDisableYear}
                desktopModeMediaQuery={desktopMediaQuery}
                showDaysOutsideCurrentMonth={true}
                value={value}
                slots={{
                    day: DayRenderer,
                    switchViewIcon: props => {
                        return (
                            <MuiIcon
                                {...props}
                                sx={theme => ({
                                    color: theme.modernFamlyTheme.colorPalette.n400,
                                    // The following ensures that MUI's default rotation transition is kept intact
                                    transitionProperty: 'transform',
                                })}
                            >
                                expand_more
                            </MuiIcon>
                        );
                    },
                    openPickerButton: props => {
                        return (
                            <MuiIconButton {...props} size="small">
                                <ModernFamlyIcon name="calendar_today" size={size === 'regular' ? 24 : 20} />
                            </MuiIconButton>
                        );
                    },

                    // Disables the date picker from showing a large headline with the selected date on mobile
                    toolbar: () => null,

                    actionBar: MobileActionBar,

                    mobilePaper: props => <Paper {...props} sx={{borderRadius: 6}} />,
                }}
            />
        </DatePickersLocalizationProvider>
    );
};

const useSyncContextWithOpenState = ({onOpen, onClose}: Pick<DatePickerProps, 'onOpen' | 'onClose'>) => {
    const {isDisplayingDatePicker, setIsDisplayingDatePicker} = React.useContext(ModernFamlyContext);

    const handleOnOpen = React.useCallback(() => {
        onOpen?.();
        setIsDisplayingDatePicker(true);
    }, [setIsDisplayingDatePicker, onOpen]);

    const handleOnClose = React.useCallback(() => {
        onClose?.();
        setIsDisplayingDatePicker(false);
    }, [setIsDisplayingDatePicker, onClose]);

    React.useEffect(() => {
        // If the date picker is open, but un-mounted, we reset the context's state.
        return () => {
            if (isDisplayingDatePicker) {
                setIsDisplayingDatePicker(false);
            }
        };
    }, [isDisplayingDatePicker, setIsDisplayingDatePicker]);

    return {
        handleOnOpen,
        handleOnClose,
    };
};

const MobileActionBar = (props: PickersActionBarProps) => {
    const {onAccept, onCancel, actions, className} = props;
    const localeText = useLocaleText();

    if (!hasValue(actions) || actions.length === 0) {
        return null;
    }

    const menuItems = actions?.map(actionType => {
        switch (actionType) {
            case 'cancel':
                return (
                    <MuiButton
                        onClick={onCancel}
                        key={actionType}
                        size="compact"
                        variant="secondary"
                        sx={{flex: '1 1 50%'}}
                    >
                        {localeText.cancelButtonLabel}
                    </MuiButton>
                );
            case 'accept':
                return (
                    <MuiButton
                        onClick={onAccept}
                        key={actionType}
                        size="compact"
                        variant="primary"
                        sx={{flex: '1 1 50%'}}
                    >
                        {localeText.okButtonLabel}
                    </MuiButton>
                );

            case 'clear':
            case 'today':
            default:
                return null;
        }
    });

    return (
        <DialogActions
            className={className}
            sx={{
                margin: '0 auto',
                width: '320px',
                px: MF_DATE_DIALOG_PADDING,
                pt: 8,
                pb: MF_DATE_DIALOG_PADDING,
                boxSizing: 'border-box',
            }}
        >
            {menuItems}
        </DialogActions>
    );
};

export const DayRenderer = (props: PickersDayProps<Moment>) => {
    const {day} = props;
    const isoWeekday = day.isoWeekday();
    const date = React.useMemo(() => day.format('YYYY-MM-DD'), [day]);
    const isWeekendDay = isoWeekday === 6 || isoWeekday === 7;

    return (
        <PickersDay
            {...props}
            sx={theme => ({
                borderRadius: 3,
                fontSize: theme.typography['body-small'].fontSize,
                lineHeight: '1em',
                width: '32px',
                height: '32px',
                margin: 0,

                // Default styles for the days
                [`&.${pickersDayClasses.root}`]: {
                    color: theme.modernFamlyTheme.colorPalette[isWeekendDay ? 'n200' : 'n400'],
                    '&:hover, &:focus': {
                        backgroundColor: theme.modernFamlyTheme.colorPalette.p100,
                    },
                },
                // Ensure that the selected day gets highlighted correctly
                [`&.${pickersDayClasses.selected}`]: {
                    backgroundColor: theme.modernFamlyTheme.colorPalette.p400,
                    color: theme.modernFamlyTheme.colorPalette.n0,
                    '&:hover, &:focus': {
                        backgroundColor: theme.modernFamlyTheme.colorPalette.p400,
                    },
                    [`&.${pickersDayClasses.dayOutsideMonth}`]: {
                        color: theme.modernFamlyTheme.colorPalette.n0,
                    },
                },
                // Ensure that disabled days are highlighted correctly
                [`&.${pickersDayClasses.disabled}`]: {
                    backgroundColor: theme.modernFamlyTheme.colorPalette.n75,
                    color: theme.modernFamlyTheme.colorPalette.n300,
                },
                // Ensure that today is highlighted correctly
                [`&.${pickersDayClasses.today}`]: {
                    border: '1px solid',
                    borderColor: theme.modernFamlyTheme.colorPalette.p400,
                },
                // Ensure that days outside the current month are highlighted correctly
                [`&.${pickersDayClasses.dayOutsideMonth}`]: {
                    color: theme.modernFamlyTheme.colorPalette[isWeekendDay ? 'n200' : 'n300'],
                },
            })}
            data-e2e-id={date}
        />
    );
};
